// Public: /survey/:token. The person invited answers the survey (no sign-in needed).
import React, { useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { Box, Container, Paper, Typography, Button, Alert, CircularProgress, Stack, LinearProgress } from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import SurveyForm, { missingRequired, isVisible } from '../components/SurveyForm';
import LegalFooter from '../components/LegalFooter';
import { SURVEY_BY_TOKEN_QUERY, SUBMIT_SURVEY_RESPONSE_MUTATION } from '../graphql/queries';

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '');

export default function SurveyRespondPage() {
  const { token } = useParams();
  // Which link they came in through (email, WhatsApp, in-app, copied); the token says whose invitation it is
  const via = new URLSearchParams(useLocation().search).get('via') || 'link';
  const { data, loading, error } = useQuery(SURVEY_BY_TOKEN_QUERY, { variables: { token, via }, fetchPolicy: 'network-only' });
  const [answers, setAnswers] = useState({});
  const [tried, setTried] = useState(false);
  const [submit, { data: done, loading: saving, error: saveError }] = useMutation(SUBMIT_SURVEY_RESPONSE_MUTATION);
  const sv = data?.surveyByToken;
  const questions = sv?.questions || [];
  const byId = Object.fromEntries(questions.map(q => [q.id, q]));
  const shown = questions.filter(q => q.type !== 'section' && isVisible(q, answers, byId));
  const answered = shown.filter(q => { const a = answers[q.id]; return !(a === undefined || a === null || a === '' || (Array.isArray(a) && !a.length)); }).length;

  const send = async () => {
    setTried(true);
    if (missingRequired(questions, answers).length) { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    // Only the answers to questions that are shown
    const payload = Object.fromEntries(Object.entries(answers).filter(([k]) => shown.some(q => q.id === k)));
    await submit({ variables: { token, answers: payload, via } }).catch(() => {});
  };

  let body;
  if (loading) body = <Stack alignItems="center" py={6}><CircularProgress /></Stack>;
  else if (error) body = <Alert severity="error">{error.message}</Alert>;
  else if (!sv) body = <Alert severity="info">This survey link is not valid.</Alert>;
  else if (done || sv.state === 'completed') {
    body = (
      <Stack alignItems="center" spacing={2} py={4} textAlign="center">
        <FactCheckIcon color="success" sx={{ fontSize: 56 }} />
        <Typography variant="h5" fontWeight={700}>{done?.submitSurveyResponse?.thankYouMessage || sv.thankYouMessage || 'Thank you for your feedback!'}</Typography>
        <Typography color="text.secondary">{done ? 'Your answers have been recorded.' : 'You have already answered this survey.'}</Typography>
      </Stack>
    );
  } else if (sv.state === 'closed') body = <Alert severity="info">This survey has closed. Thank you for your interest.</Alert>;
  else if (sv.state === 'paused') body = <Alert severity="info">This survey is paused for the moment. Please try again later.</Alert>;
  else {
    body = (
      <>
        <Typography variant="h4" fontWeight={700} gutterBottom>{sv.title}</Typography>
        {sv.subjectLabel && <Typography color="text.secondary" mb={1}>About {sv.subjectLabel}</Typography>}
        {sv.introMessage && <Typography sx={{ whiteSpace: 'pre-line' }} mb={2}>{sv.introMessage}</Typography>}
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          {sv.anonymous ? 'Your answers are anonymous.' : sv.respondentName ? `Answering as ${sv.respondentName}.` : ''}
          {sv.expiresAt ? ` Open until ${fmt(sv.expiresAt)}.` : ''} Questions marked * are required.
        </Typography>
        <LinearProgress variant="determinate" value={shown.length ? (100 * answered) / shown.length : 0} sx={{ mb: 2, height: 6, borderRadius: 3 }} />
        {tried && missingRequired(questions, answers).length > 0 && <Alert severity="warning" sx={{ mb: 2 }}>Please answer the questions marked in red.</Alert>}
        <SurveyForm questions={questions} answers={answers} onChange={setAnswers} showErrors={tried} />
        {saveError && <Alert severity="error" sx={{ mt: 2 }}>{saveError.message}</Alert>}
        <Button variant="contained" size="large" sx={{ mt: 3 }} disabled={saving} onClick={send}>{saving ? 'Sending…' : 'Submit'}</Button>
      </>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fdf6ff', display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="md" sx={{ py: { xs: 2, sm: 5 }, flex: 1 }}>
        {sv?.brandName && <Typography variant="overline" color="primary" fontWeight={700}>{sv.brandName} · Feedback</Typography>}
        <Paper elevation={2} sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 3, mt: 1 }}>{body}</Paper>
      </Container>
      <LegalFooter />
    </Box>
  );
}
